<template>
    <div class="member-break-config">
        <!-- Source selector -->
        <div class="source-section">
            <h5 class="section-title">Break Schedule</h5>
            <p class="source-help">
                Choose where this driver's breaks come from.
            </p>
            <div class="source-options">
                <md-radio v-model="source" :value="BREAK_SOURCES.None">None</md-radio>
                <md-radio v-model="source" :value="BREAK_SOURCES.Team">Team</md-radio>
                <md-radio v-model="source" :value="BREAK_SOURCES.Custom">Custom</md-radio>
            </div>
            <div class="source-actions">
                <md-button class="md-primary" :disabled="!hasChanges()" @click="saveSource">
                    Save
                </md-button>
            </div>
        </div>

        <!-- None: explicit "no breaks" -->
        <div v-if="savedSource === BREAK_SOURCES.None" class="source-detail">
            <p class="no-result-message">Breaks are disabled for this driver.</p>
        </div>

        <!-- Team: inherits team config -->
        <div v-else-if="savedSource === BREAK_SOURCES.Team" class="source-detail">
            <p class="inherit-message">
                This driver inherits the team break schedule. Edit it under Team Settings &rsaquo; Break Management.
            </p>
        </div>

        <!-- Custom: this driver's own rows -->
        <div v-else-if="savedSource === BREAK_SOURCES.Custom" class="source-detail">
            <div class="custom-toolbar">
                <div class="custom-toolbar-start">
                    <h6 class="title">Custom Break Schedule</h6>
                </div>
                <div class="custom-toolbar-end">
                    <md-button
                        class="md-round md-just-icon md-primary"
                        title="Add Break Schedule"
                        @click="setAddBreakState(true)"
                    >
                        <md-icon>add</md-icon>
                    </md-button>
                </div>
            </div>

            <div v-if="breaksList.length != 0 || addBreakState">
                <md-table class="context-menu-support custom-paginated-table">
                    <draggable
                        v-model="breaksList"
                        draggable=".drag-item"
                        class="custom-draggable"
                        @change="breakScheduleOrderChanged"
                    >
                        <md-table-row>
                            <md-table-head class="order-column"></md-table-head>
                            <md-table-head>Break Schedule</md-table-head>
                            <md-table-head>Action</md-table-head>
                        </md-table-row>
                        <team-break-row-component
                            v-for="item in breaksList"
                            :key="item.breakScheduleId"
                            :team-break-details="item"
                            :break-count="breaksList.length"
                            @editBreakScheduleClick="editBreakSchedule"
                            @deleteBreakScheduleClick="deleteBreakSchedule"
                        ></team-break-row-component>

                        <md-table-row v-if="addBreakState">
                            <md-table-cell class="order-align">
                                {{ breaksList.length + 1 }}
                            </md-table-cell>
                            <md-table-cell>
                                Before completing
                                <input
                                    type="text"
                                    class="minutes-textbox"
                                    v-model="newEntry.drivingTimeMinutes"
                                    onkeypress="return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57))"
                                />
                                minutes of work take a
                                <input
                                    type="text"
                                    class="minutes-textbox"
                                    v-model="newEntry.breakTimeMinutes"
                                    onkeypress="return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57))"
                                />
                                minute break.
                            </md-table-cell>
                            <md-table-cell>
                                <md-button
                                    class="md-round md-just-icon md-primary break-btn-icon"
                                    title="Add Break Schedule"
                                    @click="createBreakSchedule"
                                >
                                    <md-icon>check</md-icon>
                                </md-button>
                                <md-button
                                    class="md-round md-just-icon md-danger break-btn-icon"
                                    title="Cancel"
                                    @click="setAddBreakState(false)"
                                >
                                    <md-icon>cancel</md-icon>
                                </md-button>
                            </md-table-cell>
                        </md-table-row>
                    </draggable>
                </md-table>
            </div>
            <!-- Custom + empty: explicitly "no breaks", distinct from inheriting the team -->
            <div v-if="breaksList.length == 0 && !addBreakState" class="empty-custom">
                <p class="no-result-message">
                    This driver takes no breaks (custom schedule with no rows). This is different from inheriting the
                    team schedule &mdash; add a row above, or switch to <strong>Team</strong> to inherit the team's breaks.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { handleRequests } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import { TeamBreakRowComponent } from '@/pages/TeamSettings/BreakManagement/components';

const BREAK_SOURCES = {
    None: 'None',
    Team: 'Team',
    Custom: 'Custom'
};

export default {
    name: 'MemberBreakConfiguration',
    components: {
        TeamBreakRowComponent
    },
    mixins: [GeneralMixin],
    props: {
        publicUserId: {
            type: String,
            default: null
        },
        userId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            BREAK_SOURCES,
            source: BREAK_SOURCES.None,
            savedSource: BREAK_SOURCES.None,
            breaksList: [],
            newEntry: {},
            addBreakState: false
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    async mounted() {
        await this.loadSource();
    },
    methods: {
        hasChanges() {
            return this.source !== this.savedSource;
        },
        async loadSource() {
            this.$_handleLoaderState(true);
            try {
                const response = await handleRequests(`/api/breaks/source`, {
                    params: { userId: this.userId }
                });
                this.savedSource = response?.data?.source ?? BREAK_SOURCES.None;
                this.source = this.savedSource;
                if (this.savedSource === BREAK_SOURCES.Custom) {
                    await this.retrieveBreakList();
                }
            } catch (e) {
                this.$notify({ message: 'Could not load break configuration.', type: 'danger' });
            }
            this.$_handleLoaderState(false);
        },
        async saveSource() {
            this.$_handleLoaderState(true);
            try {
                await handleRequests(`/api/breaks/set-source`, {
                    method: 'post',
                    params: { userId: this.userId, source: this.source }
                });
                this.savedSource = this.source;
                this.$notify({ message: 'Break schedule source updated.', type: 'success' });
                if (this.savedSource === BREAK_SOURCES.Custom) {
                    await this.retrieveBreakList();
                } else {
                    this.breaksList = [];
                    this.setAddBreakState(false);
                }
            } catch (e) {
                this.$notify({ message: 'Could not update break schedule source.', type: 'danger' });
            }
            this.$_handleLoaderState(false);
        },
        async fetchbreaksList() {
            this.setAddBreakState(false);
            return handleRequests(`/api/breaks/list`, {
                params: { teamId: this.user.teamId, userId: this.userId }
            });
        },
        async retrieveBreakList() {
            const {
                data: { breaks }
            } = await this.fetchbreaksList();
            this.breaksList = breaks;
        },
        setAddBreakState(state) {
            this.addBreakState = state;
            if (this.addBreakState) {
                this.newEntry = {};
            }
        },
        async createBreakSchedule() {
            if (!this.validateBreakSchedule()) {
                this.$notify({ message: 'Minutes should not be empty or zero.', type: 'danger' });
                return;
            }
            this.$_handleLoaderState(true);
            this.newEntry.teamId = this.user.teamId;
            this.newEntry.userId = this.userId;
            const payload = { method: 'post', data: this.newEntry };
            try {
                await handleRequests(`/api/breaks/create-break-schedule`, payload);
                await this.retrieveBreakList();
                this.$notify({ message: 'Successfully created break schedule.', type: 'success' });
                this.newEntry = {};
                this.addBreakState = false;
            } catch (e) {
                this.$notify({ message: 'Could not create break schedule.', type: 'danger' });
            }
            this.$_handleLoaderState(false);
        },
        async editBreakSchedule(obj) {
            this.$_handleLoaderState(true);
            const payload = { method: 'post', data: { ...obj, userId: this.userId } };
            try {
                await handleRequests(`/api/breaks/update-break-schedule`, payload);
                await this.retrieveBreakList();
                this.$notify({ message: 'Successfully updated break schedule.', type: 'success' });
            } catch (e) {
                this.$notify({ message: 'Could not update break schedule.', type: 'danger' });
            }
            this.$_handleLoaderState(false);
        },
        async deleteBreakSchedule(id) {
            this.$_handleLoaderState(true);
            const payload = { method: 'post', params: { breakScheduleId: id, userId: this.userId } };
            try {
                await handleRequests(`/api/breaks/delete-break-schedule`, payload);
                await this.retrieveBreakList();
                this.$notify({ message: 'Successfully deleted break schedule.', type: 'success' });
            } catch (e) {
                this.$notify({ message: 'Could not delete break schedule.', type: 'danger' });
            }
            this.$_handleLoaderState(false);
        },
        async changeOrderBreakSchedule(id, newIndex) {
            this.$_handleLoaderState(true);
            const payload = { method: 'post', params: { breakScheduleId: id, newIndex, userId: this.userId } };
            try {
                await handleRequests(`/api/breaks/change-order-break-schedule`, payload);
                await this.retrieveBreakList();
                this.$notify({ message: 'Successfully updated break schedule.', type: 'success' });
            } catch (e) {
                this.$notify({ message: 'Could not update break schedule.', type: 'danger' });
            }
            this.$_handleLoaderState(false);
        },
        breakScheduleOrderChanged(event) {
            this.changeOrderBreakSchedule(this.breaksList[event.moved.oldIndex].breakScheduleId, event.moved.newIndex);
        },
        validateBreakSchedule() {
            if (this.newEntry === null || this.newEntry === undefined) {
                return false;
            }
            return (
                this.isPropertyValid(this.newEntry.drivingTimeMinutes) &&
                this.isPropertyValid(this.newEntry.breakTimeMinutes)
            );
        },
        isPropertyValid(property) {
            if (property === null || property === undefined || property === '' || property <= 0)
                return false;
            return true;
        }
    }
};
</script>

<style lang="scss" scoped>
.member-break-config {
    padding: 10px 5px;
}

.section-title {
    font-weight: 600;
    margin-bottom: 4px;
}

.source-help {
    color: #999;
    margin-bottom: 8px;
}

.source-options {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.source-actions {
    margin-top: 10px;
}

.source-detail {
    margin-top: 20px;
    border-top: 1px solid #eee;
    padding-top: 15px;
}

.custom-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.no-result-message {
    color: #666;
}

.inherit-message {
    color: #666;
}

.empty-custom .no-result-message {
    color: #b26a00;
}

.order-column {
    width: 100px;
}

.custom-draggable {
    display: table;
    width: 100%;
    table-layout: fixed;
}

.drag-item {
    cursor: grab;
}

.order-align {
    text-align: center;
}

.break-btn-icon {
    margin-left: 3px;
    margin-right: 3px;
}

.minutes-textbox {
    width: 30px;
}
</style>
